import React from 'react';
import { useLessonTimer } from '../../hooks/useLessonTimer';
import { Box, Alert, Divider } from '@mui/material';
import VideoLessonPlayer from './players/VideoLessonPlayer';
import DocumentLessonPlayer from './players/DocumentLessonPlayer';
import InfoTextPlayer from './players/InfoTextPlayer';
import MultipleChoicePlayer from './players/MultipleChoicePlayer';
import FreeTextPlayer from './players/FreeTextPlayer';
import FillInBlankPlayer from './players/FillInBlankPlayer';
import DragDropPlayer from './players/DragDropPlayer';
import MatchingPlayer from './players/MatchingPlayer';
import SortingPlayer from './players/SortingPlayer';
import HotspotPlayer from './players/HotspotPlayer';
import TimelinePlayer from './players/TimelinePlayer';
import ScormPlayer from './players/ScormPlayer';
import H5PPlayer from './players/H5PPlayer';
import { PeerReviewSubmit } from '../PeerReview';

interface LessonPlayerProps {
    lesson: {
        id: number;
        title: string;
        type: string;
        description?: string;
        content?: any;
        hasPeerReview?: boolean;
    };
    courseId?: number;
    userId: number;
    userEmail: string;
    onComplete: () => void;
    onProgressUpdate?: (lessonId: number, progressData: {
        status?: string;
        progressPercentage?: number;
        score?: string;
        timeSpentSeconds?: number;
        suspendData?: any;
    }) => Promise<boolean>;
}

const LessonPlayer: React.FC<LessonPlayerProps> = ({ lesson, courseId, userId, userEmail, onComplete, onProgressUpdate }) => {
    useLessonTimer(lesson.id);

    const renderPlayer = () => {
        switch (lesson.type) {
            case 'video':
                return <VideoLessonPlayer lesson={lesson} courseId={courseId} userId={userId} userEmail={userEmail} onComplete={onComplete} />;
            case 'document':
                return <DocumentLessonPlayer lesson={lesson} courseId={courseId} userId={userId} userEmail={userEmail} onComplete={onComplete} />;
            case 'info_text':
                return <InfoTextPlayer lesson={lesson} courseId={courseId} userId={userId} userEmail={userEmail} onComplete={onComplete} />;
            case 'multiple_choice':
                return <MultipleChoicePlayer lesson={lesson} onComplete={onComplete} onProgressUpdate={onProgressUpdate} />;
            case 'free_text':
                return <FreeTextPlayer lesson={lesson} onComplete={onComplete} />;
            case 'drag_drop':
                return <DragDropPlayer lesson={lesson} onComplete={onComplete} onProgressUpdate={onProgressUpdate} />;
            case 'matching':
                return <MatchingPlayer lesson={lesson} onComplete={onComplete} onProgressUpdate={onProgressUpdate} />;
            case 'sorting':
                return <SortingPlayer lesson={lesson} onComplete={onComplete} onProgressUpdate={onProgressUpdate} />;
            case 'hotspot':
                return <HotspotPlayer lesson={lesson} onComplete={onComplete} onProgressUpdate={onProgressUpdate} />;
            case 'timeline':
                return <TimelinePlayer lesson={lesson} onComplete={onComplete} onProgressUpdate={onProgressUpdate} />;
            case 'fill_in_blank':
                return <FillInBlankPlayer lesson={lesson} onComplete={onComplete} onProgressUpdate={onProgressUpdate} />;
            case 'scorm':
                return <ScormPlayer lesson={lesson} onComplete={onComplete} onProgressUpdate={onProgressUpdate} />;
            case 'h5p':
                return <H5PPlayer lesson={lesson} onComplete={onComplete} onProgressUpdate={onProgressUpdate} />;
            case 'xapi':
                return (
                    <Alert severity="info">
                        Dieser Lektionstyp ({lesson.type}) wird noch implementiert.
                    </Alert>
                );
            default:
                return (
                    <Alert severity="warning">
                        Unbekannter Lektionstyp: {lesson.type}
                    </Alert>
                );
        }
    };

    return (
        <Box>
            {lesson.description && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    {lesson.description}
                </Alert>
            )}
            {renderPlayer()}

            {/* Peer Review Section */}
            {lesson.hasPeerReview && (
                <>
                    <Divider sx={{ my: 4 }} />
                    <PeerReviewSubmit
                        lessonId={lesson.id}
                        lessonTitle={lesson.title}
                    />
                </>
            )}
        </Box>
    );
};

export default LessonPlayer;
